import React, {useCallback, useEffect, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {styled, Stack, Collapse} from '@mui/material';
import downloadAuthenticatedCsv from '@/utils/downloadAuthenticatedCsv';
import {generateSharedListUrl} from '@/utils/generateSharedListUrl';
import getCreatorProfilePageUrl from '@/utils/getCreatorProfilePageUrl';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import queryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import {PREVIEW_CREATOR_ID_QUERY_PARAM} from '@/config/routesConstants';
import EmptyState from '@/views/campaigns/components/content-submissions/empty-state';
import CreatorAction, {
  type CreatorActionKey,
} from '@/views/creators/components/creator-action/CreatorAction';
import CreatorsActionsBar from '@/views/creators/components/creators-actions-bar/CreatorsActionsBar';
import {ShareList} from '@/views/creators/lists/components/modals';
import usePreservedSearchString from '@/components/brand-header/child-components/breadcrumbs-header/usePreservedSearchString';
import ChangeDueDateModal from '@/components/change-due-date-modal';
import {ColumnDefinition} from '@/components/column-selector/ColumnSelector';
import {PROFILE_SOURCE} from '@/components/creator-profile/config/profileSources';
import {PROFILE_SOURCE_QUERY_PARAM} from '@/components/creator-profile/hooks/use-profile-source';
import CreatorSidePanel from '@/components/creator-side-panel';
import {SIDE_PANEL_TAB} from '@/components/creator-side-panel/types';
import FlashMessage from '@/components/flash-message';
import FloatingPopperBar from '@/components/floating-popper-bar';
import Modal from '@/components/modal';
import Sidebar from '@/components/sidebar';
import useShortlist from '@/hooks/mutations/use-shortlist-update';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useBrandCreatorGroupsQuery from '@/hooks/queries/use-brand-creator-groups-query';
import useCampaignQuery from '@/hooks/queries/use-campaign-query';
import useCampaignV2CreatorsCountsQuery from '@/hooks/queries/use-campaign-v2-creators-counts-query';
import useCampaignV2CreatorsQuery from '@/hooks/queries/use-campaign-v2-creators-query';
import useCreatorGroupsLabelsQuery from '@/hooks/queries/use-creator-groups-labels-query';
import useProposalAnswersQuery from '@/hooks/queries/use-proposal-answers-query';
import useBrandId from '@/hooks/use-brand-id';
import {useDebounce} from '@/hooks/use-debounce';
import useLocalStorage from '@/hooks/use-local-storage';
import useNavigation from '@/hooks/use-navigation';
import useCopyToClipboardWithFeedback from '@/hooks/useCopyToClipboardWithFeedback';
import styles from './CampaignCreatorsTab.module.scss';
import BulkMessageModal from './components/bulk-message-modal';
import CreatorsCardGrid from './components/creators-card-grid';
import CreatorsTable from './components/creators-table';
import CreatorsToolbar from './components/creators-toolbar';
import InviteLinkModal from './components/invite-link-modal';
import ShortlistEmptyState from './components/shortlist-empty-state';
import ShortlistSummaryBar from './components/shortlist-summary-bar';
import useBulkCreatorActions from './hooks/use-bulk-creator-actions';
import useCreatorKeyboardNav from './hooks/use-creator-keyboard-nav';
import useCreatorsTabState from './hooks/use-creators-tab-state';
import {CREATOR_STATUS, VIEW_TYPE} from './types/creatorTabTypes';
import type {
  CampaignCreatorRow,
  CreatorStatus,
  CreatorsViewMode,
  CampaignDeliverables,
  PageSelectionState,
} from './types/creatorTabTypes';
import {getDefaultFulfillmentSortId} from './utils/getFulfillmentActionPriority';

const CreatorsTabContainer = styled(Stack)({
  flexDirection: 'row',
});

const CreatorsTabContent = styled(Stack)({
  flex: 1,
});

const BULK_ACTION_BAR_PADDING_BOTTOM = 100;

const VALID_STATUSES = new Set<string>(Object.values(CREATOR_STATUS));
const DEFAULT_STATUS = CREATOR_STATUS.Applications;
const VALID_VIEW_TYPES = new Set<string>(Object.values(VIEW_TYPE));
const DEFAULT_VIEW_TYPE = VIEW_TYPE.Table;

const TOOLBAR_COLUMN = {
  creatorName: {id: 'creatorName', label: translate('Creator Name')},
  followers: {id: 'followers', label: translate('Followers'), canPin: true},
  engRate: {id: 'engagementRate', label: translate('Eng Rate'), canPin: true},
  lists: {id: 'lists', label: translate('Lists'), canPin: true},
  safeCollab: {
    id: 'safeCollabStatus',
    label: translate('Safe Collab Status'),
    canPin: true,
  },
  offer: {id: 'costInCents', label: translate('Offer'), canPin: true},
  history: {
    id: 'campaignHistoryCount',
    label: translate('History'),
    canPin: true,
  },
  applied: {id: 'appliedDate', label: translate('Applied'), canPin: true},
  notes: {id: 'brandNote', label: translate('Notes'), canPin: true},
  assets: {id: 'assets', label: translate('Assets'), canPin: true},
  shippingAddress: {
    id: 'shippingAddressFormatted',
    label: translate('Shipping address'),
    canPin: true,
  },
  shippingStatus: {
    id: 'shippingStatus',
    label: translate('Shipping status'),
    canPin: true,
  },
  trackingCode: {
    id: 'trackingCode',
    label: translate('Tracking #'),
    canPin: true,
  },
  giftCode: {
    id: 'giftCode',
    label: translate('Gift code'),
    canPin: true,
  },
  fulfillmentStatus: {
    id: 'fulfillmentStatus',
    label: translate('Fulfillment status'),
    canPin: true,
  },
  contentDue: {id: 'contentDue', label: translate('Content due'), canPin: true},
  creatorLocation: {
    id: 'location',
    label: translate('Creator Location'),
    canPin: true,
  },
  audienceLocation: {
    id: 'audienceTopCountry',
    label: translate('Audience Location'),
    canPin: true,
  },
  avgViews: {
    id: 'averageViews',
    label: translate('Avg Views'),
    canPin: true,
  },
} as const;

// Opt-in columns: hidden by default to keep the Applications table lean on
// first load. Users can toggle them back on from the column selector when
// they need them.
const VISIBLE_COLUMNS_STORAGE_KEY = 'campaign-creators-tab-visible-columns';

const OFF_BY_DEFAULT_COLUMN_IDS = new Set<string>([
  TOOLBAR_COLUMN.safeCollab.id,
  TOOLBAR_COLUMN.history.id,
  TOOLBAR_COLUMN.creatorLocation.id,
  TOOLBAR_COLUMN.audienceLocation.id,
  TOOLBAR_COLUMN.avgViews.id,
]);

function getHiredToolbarColumns(
  hasFulfillment: boolean,
  deliverablesType: CampaignDeliverables | null | undefined
): ColumnDefinition[] {
  const base = [
    TOOLBAR_COLUMN.creatorName,
    TOOLBAR_COLUMN.lists,
    TOOLBAR_COLUMN.notes,
    TOOLBAR_COLUMN.assets,
  ];

  if (!hasFulfillment || !deliverablesType) {
    return [
      ...base,
      TOOLBAR_COLUMN.shippingAddress,
      TOOLBAR_COLUMN.shippingStatus,
      TOOLBAR_COLUMN.contentDue,
      TOOLBAR_COLUMN.safeCollab,
    ];
  }

  if (deliverablesType === 'shipments') {
    return [
      ...base,
      TOOLBAR_COLUMN.shippingAddress,
      TOOLBAR_COLUMN.trackingCode,
      TOOLBAR_COLUMN.fulfillmentStatus,
      TOOLBAR_COLUMN.contentDue,
      TOOLBAR_COLUMN.safeCollab,
    ];
  }

  return [
    ...base,
    TOOLBAR_COLUMN.giftCode,
    TOOLBAR_COLUMN.fulfillmentStatus,
    TOOLBAR_COLUMN.contentDue,
    TOOLBAR_COLUMN.safeCollab,
  ];
}

const TOOLBAR_COLUMNS_BY_STATUS: Record<CreatorStatus, ColumnDefinition[]> = {
  [CREATOR_STATUS.Applications]: [
    TOOLBAR_COLUMN.creatorName,
    TOOLBAR_COLUMN.followers,
    TOOLBAR_COLUMN.engRate,
    TOOLBAR_COLUMN.avgViews,
    TOOLBAR_COLUMN.creatorLocation,
    TOOLBAR_COLUMN.audienceLocation,
    TOOLBAR_COLUMN.lists,
    TOOLBAR_COLUMN.offer,
    TOOLBAR_COLUMN.history,
    TOOLBAR_COLUMN.applied,
    TOOLBAR_COLUMN.safeCollab,
  ],
  [CREATOR_STATUS.Hired]: getHiredToolbarColumns(false, null),
  [CREATOR_STATUS.Declined]: [
    TOOLBAR_COLUMN.creatorName,
    TOOLBAR_COLUMN.followers,
    TOOLBAR_COLUMN.engRate,
    TOOLBAR_COLUMN.lists,
    TOOLBAR_COLUMN.offer,
    TOOLBAR_COLUMN.history,
    TOOLBAR_COLUMN.applied,
    TOOLBAR_COLUMN.safeCollab,
  ],
};

function isValidStatus(value: string | null): value is CreatorStatus {
  return value !== null && VALID_STATUSES.has(value);
}

const SHARE_TYPE = 'contracts' as const;
const CONTENT_LINK_COPIED_RESET_MS = 2000;

function buildContentShareLink(campaignId: string): string {
  return generateSharedListUrl({
    refId: campaignId,
    type: SHARE_TYPE,
    includePosts: true,
    includeNetworkAnalytics: true,
    includePortfolio: true,
  });
}

function isValidCreatorsViewMode(
  value: string | null
): value is CreatorsViewMode {
  return value !== null && VALID_VIEW_TYPES.has(value);
}

function CampaignCreatorsTab() {
  const brandId = useBrandId();
  const {navigate} = useNavigation();
  const preservedSearch = usePreservedSearchString();
  const {campaignId} = useParams<{campaignId: string}>();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('creatorStatus');
  const {counts, isFetched: isCountsFetched} = useCampaignV2CreatorsCountsQuery(
    {
      brandId,
      campaignId,
    }
  );
  // When the URL doesn't specify a tab and the campaign is past the
  // applications phase (no pending applicants but some accepted creators),
  // default to the Accepted tab so the user lands on the work that's still
  // active. An explicit tab in the URL always wins, so deep links are honored.
  const isPastApplicationsPhase =
    isCountsFetched &&
    (counts.applications ?? 0) === 0 &&
    (counts.hired ?? 0) > 0;
  const fallbackStatus = isPastApplicationsPhase
    ? CREATOR_STATUS.Hired
    : DEFAULT_STATUS;
  const activeStatus: CreatorStatus = isValidStatus(statusParam)
    ? statusParam
    : fallbackStatus;

  const viewTypeParam = searchParams.get('viewType');
  const viewType: CreatorsViewMode = isValidCreatorsViewMode(viewTypeParam)
    ? viewTypeParam
    : DEFAULT_VIEW_TYPE;

  const setActiveStatus = useCallback(
    (status: CreatorStatus) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('creatorStatus', status);
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  const setCreatorsViewMode = useCallback(
    (type: CreatorsViewMode) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('viewType', type);
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  const {sorting, setSorting} = useCreatorsTabState();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [selectedCreatorIds, setSelectedCreatorIds] = useState<Set<string>>(
    new Set()
  );
  // Grid-only multi-select toggle. The Table view always exposes checkboxes
  // via its first column (matching CRM), so this flag only gates the Grid
  // view's checkbox overlays. Toggling off also clears the selection so the
  // bulk action bar closes — matches the CRM `toggleSelectMode` behavior.
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [activeCreatorAction, setActiveCreatorAction] =
    useState<CreatorActionKey | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isBulkDueDateOpen, setIsBulkDueDateOpen] = useState(false);
  const [isBulkMessageOpen, setIsBulkMessageOpen] = useState(false);
  // Set when the grid card "Message" CTA targets a single hired creator;
  // reuses the same modal as the bulk-bar "Message" action.
  const [messageTargetCreator, setMessageTargetCreator] =
    useState<CampaignCreatorRow | null>(null);
  const {copied: contentLinkCopied, copyToClipboard: copyContentLink} =
    useCopyToClipboardWithFeedback(CONTENT_LINK_COPIED_RESET_MS);
  // Several columns are opt-in to keep the Applications table lean on first
  // load — see OFF_BY_DEFAULT_COLUMN_IDS. Users can still toggle them on from
  // the column selector when needed.
  const [visibleColumns, setVisibleColumns] = useLocalStorage<
    Record<string, boolean>
  >(
    VISIBLE_COLUMNS_STORAGE_KEY,
    Object.fromEntries(
      Object.values(TOOLBAR_COLUMN).map((col) => [
        col.id,
        !OFF_BY_DEFAULT_COLUMN_IDS.has(col.id),
      ])
    )
  );

  const {campaign, isFetched: isCampaignFetched} = useCampaignQuery({
    campaignId,
  });
  const campaignHasFulfillment = !!campaign?.hasFulfillment;
  const deliverablesType = campaign?.deliverablesType ?? null;
  const hideViewToggle =
    campaignHasFulfillment && activeStatus === CREATOR_STATUS.Hired;

  useEffect(() => {
    if (hideViewToggle && viewType === VIEW_TYPE.Grid) {
      setCreatorsViewMode(VIEW_TYPE.Table);
    }
  }, [hideViewToggle, viewType, setCreatorsViewMode]);

  useEffect(() => {
    if (activeStatus === CREATOR_STATUS.Hired) {
      if (!isCampaignFetched) return;
      if (campaignHasFulfillment && deliverablesType) {
        setSorting([
          {id: getDefaultFulfillmentSortId(deliverablesType), desc: false},
        ]);
      } else {
        // No client-side default sort: the API already orders the hired list
        // by importance (completed last, unread first). An empty sort state
        // makes the table preserve that server order across pages.
        setSorting([]);
      }
      return;
    }

    setSorting([{id: 'appliedDate', desc: true}]);
  }, [
    activeStatus,
    isCampaignFetched,
    campaignHasFulfillment,
    deliverablesType,
    setSorting,
  ]);

  const {currentBrand, allowEditCreatorBid} = useAgencyQuery({
    brandId: brandId ?? '',
  });

  const pricingVisible =
    !campaign?.hideInfluencerPricing && !!allowEditCreatorBid;

  const toolbarColumns =
    activeStatus === CREATOR_STATUS.Hired
      ? getHiredToolbarColumns(campaignHasFulfillment, deliverablesType)
      : TOOLBAR_COLUMNS_BY_STATUS[activeStatus];

  const {creatorGroups} = useBrandCreatorGroupsQuery({
    brandId,
    refetchOnWindowFocus: false,
  });
  useCreatorGroupsLabelsQuery({
    creatorGroupId: creatorGroups[0]?.id ?? '',
    refetchOnWindowFocus: false,
  });

  // Shortlist filter is only meaningful on the Applications tab — mirrors the
  // v1 behavior. The URL param is parsed truthily so deep links are shareable.
  const shortlistFilterParam = searchParams.get('shortlisted');
  const isShortlistFilterActive =
    activeStatus === CREATOR_STATUS.Applications &&
    shortlistFilterParam === 'true';

  const [isUnreadFilterActive, setIsUnreadFilterActive] = useState(false);

  const {creators, fetchNextPage, isFetching} = useCampaignV2CreatorsQuery({
    brandId,
    campaignId,
    status: activeStatus,
    search: debouncedSearch,
    shortlisted: isShortlistFilterActive ? true : undefined,
    hasUnreadMessages: isUnreadFilterActive ? true : undefined,
  });

  const shortlist = useShortlist(campaignId, creators);
  const {removeMany: removeShortlistCreators} = shortlist;

  const setShortlistFilterActive = useCallback(
    (active: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (active) {
            next.set('shortlisted', 'true');
          } else {
            next.delete('shortlisted');
          }
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  const handleToggleShortlistFilter = useCallback(() => {
    setShortlistFilterActive(!isShortlistFilterActive);
  }, [isShortlistFilterActive, setShortlistFilterActive]);

  // Wipes every shortlisted creator in this campaign. Intentionally has no
  // confirmation step (matches the product decision) — the banner vanishing
  // and the empty state taking its place provide immediate visual feedback,
  // and the flash message reports exactly how many were removed.
  const handleClearShortlist = useCallback(() => {
    const idsToClear = creators.filter((c) => c.isShortlisted).map((c) => c.id);
    if (idsToClear.length === 0) return;
    removeShortlistCreators(idsToClear);
    raiseFlashMessage({
      status: 'success',
      mode: 'light',
      message: (
        <FlashMessage.Banner
          title={translate('Shortlist cleared')}
          subtitle={translate('{count} creators removed', {
            count: idsToClear.length,
          })}
        />
      ),
    });
  }, [creators, removeShortlistCreators]);

  // Brand-facing shortlist total (creator cost + markup) from the campaign
  // record, not a sum of visible rows. `null` when pricing isn't visible; a
  // real $0 is preserved so the bar can show "$0.00".
  const shortlistTotalCost = pricingVisible
    ? (campaign?.shortlistedProposalsTotalPrice ?? null)
    : null;

  // When the filter is on but nothing is left to render, show a focused
  // empty state instead of an empty table + meaningless "0 creators" banner.
  // `!isFetching` prevents the empty state from flashing while the initial
  // page is still loading.
  const showShortlistEmptyState =
    isShortlistFilterActive && creators.length === 0 && !isFetching;

  // Drive the per-status empty-state copy: whether the queue has been worked
  // through, and whether anyone's been hired yet.
  const hasReviewedCreators =
    (counts.hired ?? 0) > 0 || (counts.declined ?? 0) > 0;
  const hasHiredCreators = (counts.hired ?? 0) > 0;

  // Empty-state copy/action keyed by the situation. Mirrors the Figma
  // "Creators-applications"/"Creators-hired" specs; the Hired CTA switches the
  // sidebar to Applicants (same campaign) so brands can review without leaving.
  const emptyStateProps: Record<
    CreatorStatus,
    React.ComponentProps<typeof EmptyState>
  > = {
    [CREATOR_STATUS.Applications]: hasReviewedCreators
      ? {
          title: translate('You reviewed every applicant.'),
          description: hasHiredCreators
            ? translate(
                'Nice work clearing the queue. Your hired creators are getting started.'
              )
            : undefined,
        }
      : {
          title: translate('Still quiet'),
          description: translate(
            'Creators are finding your brief — usually takes a few days.'
          ),
        },
    [CREATOR_STATUS.Hired]: {
      title: translate("Your roster's empty"),
      description: translate('Head to Applicants and start building the team.'),
      action: {
        label: translate('Review Applications'),
        onClick: () => setActiveStatus(CREATOR_STATUS.Applications),
      },
    },
    [CREATOR_STATUS.Declined]: {title: translate('No declined creators')},
  };

  const unreadEmptyStateProps: React.ComponentProps<typeof EmptyState> = {
    title: translate('No unread messages'),
    description: translate(
      "You're all caught up — no creators here have unread messages."
    ),
  };

  // A search with zero results is a distinct "no matches" situation the views
  // handle with their own default, so we render none in that case.
  const activeEmptyStateProps = isUnreadFilterActive
    ? unreadEmptyStateProps
    : emptyStateProps[activeStatus];
  const creatorsEmptyState =
    debouncedSearch === '' ? (
      <EmptyState {...activeEmptyStateProps} />
    ) : undefined;

  const previewCreatorId = searchParams.get(PREVIEW_CREATOR_ID_QUERY_PARAM);
  const selectedCreator =
    creators.find((c) => c.id === previewCreatorId) ?? null;

  const {answers} = useProposalAnswersQuery(selectedCreator?.paidGigId ?? null);

  const handleRowClick = useCallback(
    (row: CampaignCreatorRow) => {
      setSearchParams(
        (prev) => {
          prev.set(PREVIEW_CREATOR_ID_QUERY_PARAM, row.id);
          return prev;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  const handleCloseSidePanel = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.delete(PREVIEW_CREATOR_ID_QUERY_PARAM);
        return prev;
      },
      {replace: true}
    );
  }, [setSearchParams]);

  // Arrow-key navigation across creators while the preview side panel is open
  // (Up = previous creator, Down = next, Escape = close panel).
  useCreatorKeyboardNav({
    isActive: selectedCreator !== null,
    creators,
    selectedCreatorId: previewCreatorId,
    onSelectCreator: (creatorId) => {
      setSearchParams(
        (prev) => {
          prev.set(PREVIEW_CREATOR_ID_QUERY_PARAM, creatorId);
          return prev;
        },
        {replace: true}
      );
    },
    onClose: handleCloseSidePanel,
  });

  // Keep the previewed row visible as the user arrow-keys through the list.
  // Table.tsx sets `id={row.id}` on each <tr>, so we can find it by DOM id.
  // `block: 'nearest'` only scrolls if the row is actually off-screen, so
  // clicking a row that's already visible doesn't cause a jarring scroll.
  useEffect(() => {
    if (!previewCreatorId) return;
    const rowElement = document.getElementById(previewCreatorId);
    rowElement?.scrollIntoView({block: 'nearest'});
  }, [previewCreatorId]);

  const handleViewFullProfile = (profileId: string) => {
    if (!campaignId) return;

    const sourceByStatus: Record<CreatorStatus, string> = {
      [CREATOR_STATUS.Applications]: PROFILE_SOURCE.APPLICATIONS,
      [CREATOR_STATUS.Hired]: PROFILE_SOURCE.ACCEPTED,
      [CREATOR_STATUS.Declined]: PROFILE_SOURCE.DECLINED,
    };

    const baseSearch = new URLSearchParams(preservedSearch);
    baseSearch.set(PROFILE_SOURCE_QUERY_PARAM, sourceByStatus[activeStatus]);
    // `creatorStatus` is the paginated-query fallback the profile page uses
    // when route state is gone (hard refresh / deep link).
    if (!baseSearch.has('creatorStatus')) {
      baseSearch.set('creatorStatus', activeStatus);
    }

    // Hand the in-memory row over so the profile page doesn't need to
    // re-derive it via the paginated query (which can miss rows on
    // not-yet-loaded pages).
    const campaignRow = creators.find((c) => c.profileId === profileId);

    navigate(
      `${getCreatorProfilePageUrl.fromCampaign(campaignId, profileId)}?${baseSearch.toString()}`,
      campaignRow ? {state: {campaignRow}} : undefined
    );
  };
  const handleActionComplete = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['campaignV2Creators'],
      exact: false,
    });

    queryClient.invalidateQueries({
      queryKey: ['campaignV2CreatorsCounts'],
      exact: false,
    });

    // Accepting/declining changes the campaign's server-computed cost
    // aggregates (`acceptedTotalPrice`) shown in the campaign cost summary.
    queryClient.invalidateQueries({queryKey: ['campaign', campaignId]});
  }, [campaignId]);

  const statusItems = [
    {
      id: CREATOR_STATUS.Applications,
      label: translate('Applications'),
      count: counts.applications ?? 0,
    },
    {
      id: CREATOR_STATUS.Hired,
      label: translate('Accepted'),
      count: counts.hired ?? 0,
    },
    {
      id: CREATOR_STATUS.Declined,
      label: translate('Declined'),
      count: counts.declined ?? 0,
    },
  ];

  const handleStatusSelect = useCallback(
    (id: string) => {
      if (isValidStatus(id)) {
        setSelectedCreatorIds(new Set());
        // Drop a pending single-creator message target alongside the bulk
        // selection — it belongs to the tab being left.
        setMessageTargetCreator(null);
        // Merge both param changes in one call so neither overwrites the other.
        // Shortlist filter is only meaningful on Applications; clear it when
        // switching tabs so the user doesn't end up looking at an empty Hired
        // or Declined tab with no obvious reason why.
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('creatorStatus', id);
            if (id !== CREATOR_STATUS.Applications) {
              next.delete('shortlisted');
            }
            return next;
          },
          {replace: true}
        );
      }
      setIsUnreadFilterActive(false);
    },
    [setSearchParams]
  );

  const selectedCreatorsList = creators.filter((c) =>
    selectedCreatorIds.has(c.id)
  );

  const messageRecipients = messageTargetCreator
    ? [messageTargetCreator]
    : selectedCreatorsList;

  const pageSelectionState = (() => {
    if (creators.length === 0 || selectedCreatorIds.size === 0) return 'none';
    const selectedOnPage = creators.filter((c) =>
      selectedCreatorIds.has(c.id)
    ).length;
    if (selectedOnPage === creators.length) return 'all';
    if (selectedOnPage > 0) return 'partial';
    return 'none';
  })() as PageSelectionState;

  const rowSelection: Record<string, boolean> = {};
  creators.forEach((creator) => {
    if (selectedCreatorIds.has(creator.id)) {
      rowSelection[creator.id] = true;
    }
  });

  const handleSelectionChange = useCallback(
    (creatorId: string, isSelected: boolean) => {
      setSelectedCreatorIds((prev) => {
        const next = new Set(prev);
        if (isSelected) {
          next.add(creatorId);
        } else {
          next.delete(creatorId);
        }
        return next;
      });
    },
    []
  );

  const handleSelectAllPage = useCallback(
    (isSelected: boolean) => {
      if (isSelected) {
        setSelectedCreatorIds(new Set(creators.map((c) => c.id)));
      } else {
        setSelectedCreatorIds(new Set());
      }
    },
    [creators]
  );

  const handleClearSelection = useCallback(() => {
    setSelectedCreatorIds(new Set());
  }, []);

  // Toggle multi-select mode. When turning OFF, also clear any selection so
  // the bulk action bar (which is driven purely by `selectedCreatorIds.size`)
  // closes — matches the CRM `toggleSelectMode` behavior.
  const handleToggleSelectMode = useCallback(() => {
    setIsSelectMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedCreatorIds(new Set());
      }
      return next;
    });
  }, []);

  const bulkActionItems = useBulkCreatorActions({
    selectedCreators: selectedCreatorsList,
    activeStatus,
    onActionComplete: handleActionComplete,
    onSetActiveAction: setActiveCreatorAction,
    onChangeDueDate: () => setIsBulkDueDateOpen(true),
    onMessage: () => setIsBulkMessageOpen(true),
    onDone: handleClearSelection,
  });

  const avatarUrls = selectedCreatorsList
    .map((c) => c.avatarUrl)
    .filter((url): url is string => url !== null)
    .slice(0, 3);

  const creatorActionProps = selectedCreatorsList.map((c) => ({
    id: c.profileId,
    name: c.creatorName,
    avatar: c.avatarUrl ?? '',
  }));

  const isActionBarOpen = selectedCreatorIds.size > 0;

  const handleColumnVisibilityChange = useCallback(
    (columnId: string, visible: boolean) => {
      setVisibleColumns({...visibleColumns, [columnId]: visible});
    },
    [visibleColumns, setVisibleColumns]
  );

  const handleShareCSV = useCallback(() => {
    if (campaignId) {
      downloadAuthenticatedCsv(SHARE_TYPE, campaignId);
    }
  }, [campaignId]);

  const handleShareContentLink = useCallback(() => {
    if (campaignId) {
      copyContentLink(buildContentShareLink(campaignId));
    }
  }, [campaignId, copyContentLink]);

  const handleOpenShareModal = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  const handleCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  const handleOpenInviteModal = useCallback(() => {
    setIsInviteModalOpen(true);
  }, []);

  const handleCloseInviteModal = useCallback(() => {
    setIsInviteModalOpen(false);
  }, []);

  const handleCreatorActionClose = useCallback(
    (reason: string) => {
      setActiveCreatorAction(null);
      if (reason === 'success') {
        handleActionComplete();
        setSelectedCreatorIds(new Set());
      }
    },
    [handleActionComplete]
  );

  const inviteLink = campaign?.directLinkBriefUrl ?? '';

  function renderCreatorsView() {
    if (viewType === VIEW_TYPE.Table) {
      return (
        <div className={styles.tableWrapper}>
          <CreatorsTable
            data={creators}
            activeStatus={activeStatus}
            hasFulfillment={campaignHasFulfillment}
            deliverablesType={deliverablesType}
            sorting={sorting}
            onSorting={setSorting}
            onActionComplete={handleActionComplete}
            onRowClick={handleRowClick}
            visibleColumns={{
              ...visibleColumns,
              costInCents: pricingVisible && visibleColumns.costInCents,
            }}
            selectedCreatorIds={selectedCreatorIds}
            pageSelectionState={pageSelectionState}
            onSelectionChange={handleSelectionChange}
            onSelectAllPage={handleSelectAllPage}
            rowSelection={rowSelection}
            onEndReached={fetchNextPage}
            isLoading={isFetching}
            highlightedRowIds={
              previewCreatorId ? new Set([previewCreatorId]) : undefined
            }
            emptyStateComponent={creatorsEmptyState}
          />
        </div>
      );
    }
    return (
      <div className={styles.gridWrapper}>
        <CreatorsCardGrid
          data={creators}
          onCardClick={handleRowClick}
          onEndReached={fetchNextPage}
          isSelectMode={isSelectMode}
          selectedCreatorIds={selectedCreatorIds}
          onSelectChange={handleSelectionChange}
          emptyStateComponent={creatorsEmptyState}
          onActionComplete={handleActionComplete}
          onMessageCreator={setMessageTargetCreator}
        />
      </div>
    );
  }

  return (
    <CreatorsTabContainer className={styles.container}>
      <Sidebar
        title={translate('Status')}
        items={statusItems}
        selectedId={activeStatus}
        onSelect={handleStatusSelect}
      />
      <FloatingPopperBar
        className={styles.bulkActionBarWrapper}
        isOpen={isActionBarOpen}
        placement="bottom"
        // The Status sidebar makes this layout asymmetric — the default
        // anchor extension would push the bar into the sidebar area on the
        // left. Match the anchor width exactly instead.
        widthCalculation={(w) => w ?? 'auto'}
        renderContent={() => (
          <CreatorsActionsBar
            avatarUrls={avatarUrls}
            selectedCount={selectedCreatorIds.size}
            actionItems={bulkActionItems}
            closeButtonProps={{
              appearance: 'neutral',
              size: 'medium',
              mode: 'plain',
            }}
            closeButtonText={translate('Clear Selection')}
            closeButtonOnClick={handleClearSelection}
          />
        )}
      >
        <CreatorsTabContent
          className={styles.content}
          style={
            isActionBarOpen
              ? {paddingBottom: BULK_ACTION_BAR_PADDING_BOTTOM}
              : undefined
          }
        >
          <CreatorsToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            showingCount={creators.length}
            onShareCSV={handleShareCSV}
            onShareCreatorsLink={handleOpenShareModal}
            onShareContentLink={handleShareContentLink}
            contentLinkCopied={contentLinkCopied}
            onInviteClick={handleOpenInviteModal}
            columns={
              pricingVisible
                ? toolbarColumns
                : toolbarColumns.filter((c) => c.id !== 'costInCents')
            }
            visibleColumns={{
              ...visibleColumns,
              costInCents: pricingVisible && visibleColumns.costInCents,
            }}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            activeCreatorsViewMode={viewType}
            onCreatorsViewModeChange={setCreatorsViewMode}
            showShortlistFilter={activeStatus === CREATOR_STATUS.Applications}
            isShortlistFilterActive={isShortlistFilterActive}
            onToggleShortlistFilter={handleToggleShortlistFilter}
            shortlistCount={shortlist.count}
            showUnreadFilter
            isUnreadFilterActive={isUnreadFilterActive}
            onToggleUnreadFilter={() =>
              setIsUnreadFilterActive((active) => !active)
            }
            isSelectMode={isSelectMode}
            onToggleSelectMode={handleToggleSelectMode}
            showViewToggle={!hideViewToggle}
          />
          {/* Smoothly slide the banner in/out so toggling the shortlist filter
            doesn't shove the table down by ~50px in a single frame. MUI's
            `Collapse` animates the height + opacity together, which keeps
            spatial continuity for the table below. */}
          <Collapse
            in={isShortlistFilterActive && !showShortlistEmptyState}
            timeout={250}
          >
            <ShortlistSummaryBar
              count={creators.length}
              totalCostInDollars={shortlistTotalCost}
              onClearShortlist={handleClearShortlist}
            />
          </Collapse>
          {showShortlistEmptyState ? (
            <ShortlistEmptyState />
          ) : (
            renderCreatorsView()
          )}
        </CreatorsTabContent>
      </FloatingPopperBar>
      {activeCreatorAction ? (
        <CreatorAction
          creators={creatorActionProps}
          activeCreatorAction={activeCreatorAction}
          close={handleCreatorActionClose}
        />
      ) : null}
      <Modal
        open={isShareModalOpen}
        onClose={handleCloseShareModal}
        size="small"
        contentClassName={styles.autoHeightModal}
      >
        <ShareList refId={campaignId ?? ''} type="contracts" />
      </Modal>
      <InviteLinkModal
        open={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        campaignName={campaign?.name ?? ''}
        inviteLink={inviteLink}
      />
      {isBulkDueDateOpen ? (
        <ChangeDueDateModal
          open
          onClose={() => setIsBulkDueDateOpen(false)}
          batchContractIds={selectedCreatorsList
            .map((c) => c.paidGigId)
            .filter((id): id is string => Boolean(id))}
          onSuccess={() => {
            setIsBulkDueDateOpen(false);
            handleClearSelection();
          }}
        />
      ) : null}
      {isBulkMessageOpen || messageTargetCreator ? (
        <BulkMessageModal
          open
          onClose={() => {
            setIsBulkMessageOpen(false);
            setMessageTargetCreator(null);
          }}
          campaignId={campaignId ?? ''}
          brandId={brandId ?? ''}
          contractIds={messageRecipients
            .map((c) => c.paidGigId)
            .filter((id): id is string => Boolean(id))}
          creators={messageRecipients
            .filter((c) => c.paidGigId)
            .map((c) => ({
              id: c.id,
              displayName: c.creatorName,
              imageUrl: c.avatarUrl ?? '',
            }))}
          totalAcceptedCount={counts.hired ?? 0}
          // "Send to all accepted" only applies to the bulk selection path.
          allLoadedSelected={
            messageTargetCreator ? false : pageSelectionState === 'all'
          }
          onSuccess={() => {
            handleClearSelection();
            setMessageTargetCreator(null);
          }}
        />
      ) : null}
      <CreatorSidePanel
        isOpen={selectedCreator !== null}
        creator={selectedCreator}
        conversationId={selectedCreator?.merchantConversationId ?? ''}
        applicationQuestions={answers}
        brandId={brandId ?? ''}
        brandName={currentBrand?.name ?? ''}
        campaignName={campaign?.name}
        campaignNetwork={campaign?.network}
        isNegotiatedTermsVisible={pricingVisible}
        campaignUsageRightsPeriodInMonths={
          Number(campaign?.contentAgreementTimePeriodInMonths) || null
        }
        defaultTab={SIDE_PANEL_TAB.QuickView}
        onClose={handleCloseSidePanel}
        onViewFullProfile={handleViewFullProfile}
      />
    </CreatorsTabContainer>
  );
}

export default CampaignCreatorsTab;
